/**
 * A WebSocket wrapper with:
 *  - Exponential back-off reconnection (1 s → 2 s → 4 s … capped at 30 s)
 *  - Configurable max retry count (default 10, 0 = unlimited)
 *  - Periodic keep-alive ping (default every 30 s)
 *  - Clean teardown via .dispose()
 */

export type ReconnectingWsOptions = {
  /** Build the full WS URL (called on every connect so tokens stay fresh). */
  url: string | (() => string);

  /** WebSocket sub-protocols (e.g. `['Bearer', token]`). */
  protocols?: string | string[] | (() => string | string[]);

  /** Called when the socket opens (or re-opens after a reconnect). */
  onOpen?: () => void;

  /** Called for every incoming message. */
  onMessage?: (event: MessageEvent) => void;

  /** Called when the connection closes (before a reconnect attempt, if any). */
  onClose?: (event: CloseEvent) => void;

  /** Called on error. */
  onError?: (event: Event) => void;

  /**
   * Called when reconnection state changes.
   * `connected` is true after a successful open, false when the socket drops.
   */
  onConnectionChange?: (connected: boolean) => void;

  /** Initial back-off delay in ms (default 1000). */
  initialDelayMs?: number;

  /** Maximum back-off delay in ms (default 30000). */
  maxDelayMs?: number;

  /** Maximum number of consecutive reconnect attempts. 0 = unlimited (default 10). */
  maxRetries?: number;

  /** Interval between keep-alive pings in ms. 0 to disable (default 30000). */
  pingIntervalMs?: number;

  /** The JSON payload sent as a keep-alive ping (default `{"type":"ping"}`). */
  pingPayload?: unknown;

  /**
   * When true, the utility automatically responds to server-sent
   * `{"type":"ping"}` frames with `{"type":"pong"}` before forwarding
   * the message to `onMessage`. (default false)
   */
  serverPingPong?: boolean;
};

export type ReconnectingWs = {
  /** Send a JSON-serialisable value over the socket. Safe to call when not connected (no-op). */
  send: (data: unknown) => void;

  /** Tear down the socket and stop all reconnection / ping timers. */
  dispose: () => void;

  /** Get the current raw WebSocket (or null). */
  raw: () => WebSocket | null;
};

export function createReconnectingWs(opts: ReconnectingWsOptions): ReconnectingWs {
  const {
    onOpen,
    onMessage,
    onClose,
    onError,
    onConnectionChange,
    initialDelayMs = 1_000,
    maxDelayMs = 30_000,
    maxRetries = 10,
    pingIntervalMs = 30_000,
    pingPayload = { type: 'ping' },
    serverPingPong = false,
  } = opts;

  let ws: WebSocket | null = null;
  let disposed = false;
  let retryCount = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let pingTimer: ReturnType<typeof setInterval> | null = null;

  const resolveUrl = (): string =>
    typeof opts.url === 'function' ? opts.url() : opts.url;

  const resolveProtocols = (): string | string[] | undefined => {
    if (!opts.protocols) return undefined;
    return typeof opts.protocols === 'function' ? opts.protocols() : opts.protocols;
  };

  /* ── Ping ── */

  function startPing() {
    stopPing();
    if (pingIntervalMs <= 0) return;
    pingTimer = setInterval(() => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify(pingPayload));
        } catch {
          // ignore – the onerror / onclose path will handle it
        }
      }
    }, pingIntervalMs);
  }

  function stopPing() {
    if (pingTimer !== null) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
  }

  /* ── Connect ── */

  function connect() {
    if (disposed) return;

    // Tear down any existing socket before creating a new one
    cleanup();

    try {
      const url = resolveUrl();
      const protocols = resolveProtocols();
      ws = protocols ? new WebSocket(url, protocols) : new WebSocket(url);
    } catch {
      scheduleReconnect();
      return;
    }

    ws.onopen = () => {
      retryCount = 0;
      onConnectionChange?.(true);
      onOpen?.();
      startPing();
    };

    ws.onmessage = (evt) => {
      // Auto-respond to server-sent {"type":"ping"} with {"type":"pong"}
      if (serverPingPong && typeof evt.data === 'string') {
        try {
          const parsed = JSON.parse(evt.data);
          if (parsed?.type === 'ping') {
            try { ws!.send(JSON.stringify({ type: 'pong' })); } catch { /* ignore */ }
            return; // don't forward keep-alive frames to the consumer
          }
        } catch {
          // not JSON — fall through to onMessage
        }
      }
      onMessage?.(evt);
    };

    ws.onerror = (evt) => {
      onError?.(evt);
    };

    ws.onclose = (evt) => {
      stopPing();
      onConnectionChange?.(false);
      onClose?.(evt);

      // Don't reconnect on normal closure (1000), policy violation / expired token (1008), or if disposed
      if (disposed) return;
      if (evt.code === 1000 || evt.code === 1008) return;

      scheduleReconnect();
    };
  }

  /* ── Reconnect ── */

  function scheduleReconnect() {
    if (disposed) return;
    if (maxRetries > 0 && retryCount >= maxRetries) return;

    const delay = Math.min(initialDelayMs * Math.pow(2, retryCount), maxDelayMs);
    retryCount += 1;

    retryTimer = setTimeout(() => {
      retryTimer = null;
      connect();
    }, delay);
  }

  /* ── Cleanup (internal, does NOT mark as disposed) ── */

  function cleanup() {
    stopPing();
    if (ws) {
      try {
        ws.onopen = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        ws.close();
      } catch {
        // ignore
      }
      ws = null;
    }
  }

  /* ── Public API ── */

  function send(data: unknown) {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(typeof data === 'string' ? data : JSON.stringify(data));
      } catch {
        // ignore
      }
    }
  }

  function dispose() {
    disposed = true;
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    cleanup();
  }

  function raw() {
    return ws;
  }

  // Kick off the first connection
  connect();

  return { send, dispose, raw };
}
